import * as React from 'react'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type {
  ColumnDef,
  Row,
  Table as TanstackTable,
} from '@tanstack/react-table'
import { cn } from '@/utils/cn'
import { Pagination } from '@/components/ui/pagination'

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

export interface DataTableProps<TData, TValue = any> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  emptyState?: React.ReactNode
  pagination?: boolean
  defaultPageSize?: number
  pageSizeOptions?: number[]
  tableOptions?: Partial<Parameters<typeof useReactTable<TData>>[0]>
  children?: (table: TanstackTable<TData>) => React.ReactNode
  headertable?: React.ReactNode
  footer?: React.ReactNode
  className?: string
  getRowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement>
}

export function DataTable<TData, TValue = any>({
  columns,
  data,
  isLoading = false,
  emptyState,
  pagination: enablePagination = false,
  defaultPageSize = 10,
  pageSizeOptions,
  tableOptions,
  children,
  headertable,
  footer,
  className,
  getRowProps,
}: DataTableProps<TData, TValue>) {
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      state: { pagination: { pageIndex, pageSize } },
      onPaginationChange: setPagination,
      manualPagination: false,
    }),
    ...tableOptions,
  })

  return (
    <div className={cn('w-full min-w-0', className)}>
      <div className="flex h-full min-h-0 min-w-0 flex-col rounded-xl border border-gray-200 bg-white">
        {headertable && (
          <div className="border-b border-gray-200 px-4 py-3">
            {headertable}
          </div>
        )}
        <div className="min-h-0 min-w-0 max-h-[calc(100vh-260px)] flex-1 overflow-x-auto overflow-y-auto">
          <table className="p-5 pb-0 w-full min-w-[700px] caption-bottom border-separate border-spacing-0 text-sm">
            <thead>
              {table.getHeaderGroups().map((hg, hgIndex) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const colDef = header.column.columnDef as any
                    const colSize = colDef.size
                    const alignClass = colDef.align
                      ? alignClasses[colDef.align as keyof typeof alignClasses]
                      : 'text-left'

                    return (
                      <th
                        key={header.id}
                        style={
                          colSize
                            ? { width: colSize, minWidth: colSize }
                            : undefined
                        }
                        className={cn(
                          'whitespace-nowrap bg-[#F5F5F5] border border-b-solid border-gray-300 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-700',
                          alignClass,
                          hgIndex === 0 &&
                            'first:rounded-tl-lg last:rounded-tr-lg',
                          header.column.id === 'actions' &&
                            'sticky right-0 z-20 bg-[#F5F5F5]',
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="relative">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-16 text-center text-sm font-medium text-gray-500"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span>Đang tải dữ liệu bảng...</span>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => {
                  const customRowProps = getRowProps?.(row)

                  return (
                    <tr
                      key={row.id}
                      {...customRowProps}
                      className={cn(
                        'group transition-colors hover:bg-gray-50 data-[state=selected]:bg-gray-50',
                        customRowProps?.className,
                      )}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const colDef = cell.column.columnDef as any
                        const colSize = colDef.size
                        const alignClass = colDef.align
                          ? alignClasses[
                              colDef.align as keyof typeof alignClasses
                            ]
                          : 'text-left'

                        return (
                          <td
                            key={cell.id}
                            style={
                              colSize
                                ? {
                                    width: colSize,
                                    minWidth: colSize,
                                  }
                                : { minWidth: '200px' }
                            }
                            className={cn(
                              'border border-b-solid border-gray-200 px-4 h-15 align-middle text-sm text-gray-900 transition-colors group-last:border-b-0',
                              alignClass,
                              colSize
                                ? 'whitespace-normal break-words'
                                : 'whitespace-nowrap',
                              cell.column.id === 'actions' &&
                                'sticky right-0 z-10 bg-white group-hover:bg-gray-50 group-data-[state=selected]:bg-gray-50',
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-16 text-center text-sm text-muted-foreground"
                  >
                    {emptyState ?? 'Không có dữ liệu'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {footer && (
          <div className="border-t border-gray-200 px-4 py-3">{footer}</div>
        )}
      </div>
      {children?.(table)}
      {enablePagination && (
        <Pagination
          page={pageIndex + 1}
          pageCount={table.getPageCount()}
          pageSize={pageSize}
          totalCount={data.length}
          pageSizeOptions={pageSizeOptions}
          onPageChange={(p) =>
            setPagination((s) => ({ ...s, pageIndex: p - 1 }))
          }
          onPageSizeChange={(size) =>
            setPagination({ pageIndex: 0, pageSize: size })
          }
        />
      )}
    </div>
  )
}
